import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../contexts/LanguageContext";
import Select from "react-select";
import "./EditVoucher.scss";
import { HiCheck, HiArrowLeft, HiArrowRight } from "react-icons/hi";
import { PageLayout, ActionButton } from "../../../components/modern";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

interface CustomerOption {
  value: string;
  label: string;
}

export const EditVoucher = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['vouchers', 'common']);
  const { isRTL } = useLanguage();

  const [formData, setFormData] = useState({
    id,
    code: "",
    voucherType: 0,
    numberOfUse: 0,
    value: 0,
    maxAmount: -1,
    from: "",
    to: "",
    isActive: false,
    isPublic: false,
    autoApply: false,
    customers: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const token = localStorage.getItem("token");
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<CustomerOption[]>([]);

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const response = await adminApi.call(`/voucher/${id}`, 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('vouchers:messages.fetchError'));
          return;
        }

        setFormData(response.data);
      } catch (error: any) {
        showErrorToast(t('common:error') + ": " + error.message);
      }
    };

    fetchItem();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "voucherType"
          ? parseInt(value)
          : value,
    }));
  };

  const handleCustomerChange = (selectedOptions: CustomerOption[] | null) => {
    const customers = selectedOptions ? selectedOptions.map(option => option.value).join(",") : "";
    setSelectedCustomers(selectedOptions || []);
    setFormData((prev) => ({
      ...prev,
      customers: customers,
    }));
  };
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await adminApi.call('/user/customers/index?pageSize=100000', 'GET');

        if (!response.success) {
          console.error("Error loading customers:", response.error);
          return;
        }

        // Format data for react-select
        const formattedOptions = response.data?.items.map((customer: any) => ({
          value: customer.id.toString(),
          label: customer.name
        }));
        setCustomerOptions(formattedOptions);
      } catch (error) {
        console.error("Error loading customers:", error);
      }
    };

    fetchCustomers();
  }, []);

  // Set selected customers when both formData and customerOptions are available
  useEffect(() => {
    if (formData.customers && customerOptions.length > 0) {
      const customerIds = formData.customers.split(",").filter((id: string) => id);
      const selectedOptions = customerOptions.filter(option =>
        customerIds.includes(option.value)
      );
      setSelectedCustomers(selectedOptions);
    }
  }, [formData.customers, customerOptions]);
  const submitUpdate = async () => {
    setLoading(true);

    try {
      const response = await adminApi.call('/voucher/admin/update', 'PUT', formData);

      if (!response.success) {
        showErrorToast(response.error || t('vouchers:messages.updateError'));
        return;
      }

      showSuccessToast(t('vouchers:messages.updateSuccess'));
    } catch (error: any) {
      showErrorToast(t('common:error') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { path: '/vouchers', label: t('vouchers:title') },
    { path: `/vouchers/edit/${id}`, label: t('vouchers:editVoucher') }
  ];

  return (
    <PageLayout
      title={t('vouchers:editVoucher')}
      subtitle={t('vouchers:subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
    >

      <div className="row">
        <div className="mb-3 col-md-6">
          <label className="form-label">{t('vouchers:fields.code')}</label>
          <input
            type="text"
            className="form-control"
            name="code"
            value={formData.code}
            onChange={handleInputChange}
            placeholder={t('vouchers:placeholders.code')}
          />
        </div>

        <div className="mb-3 col-md-6">
          <label className="form-label">{t('vouchers:fields.type')}</label>
          <select
            className="form-control"
            name="voucherType"
            value={formData.voucherType}
            onChange={handleInputChange}
          >
            <option value={0}>{t('vouchers:types.percent')}</option>
            <option value={1}>{t('vouchers:types.amount')}</option>
          </select>
        </div>

        <div className="mb-3 col-md-6">
          <label className="form-label">{t('vouchers:fields.startDate')}</label>
          <input
            type="date"
            className="form-control"
            name="from"
            value={formData.from?.substring(0, 10) || ""}
            onChange={handleInputChange}
            placeholder={t('vouchers:placeholders.startDate')}
          />
        </div>

        <div className="mb-3 col-md-6">
          <label className="form-label">{t('vouchers:fields.endDate')}</label>
          <input
            type="date"
            className="form-control"
            name="to"
            value={formData.to?.substring(0, 10) || ""}
            onChange={handleInputChange}
            placeholder={t('vouchers:placeholders.endDate')}
          />
        </div>

        <div className="mb-3 col-md-6">
          <label className="form-label">{t('vouchers:fields.value')}</label>
          <input
            type="number"
            className="form-control"
            name="value"
            value={formData.value}
            onChange={handleInputChange}
            placeholder={t('vouchers:placeholders.value')}
          />
        </div>

        <div className="mb-3 col-md-6">
          <label className="form-label">{t('vouchers:table.numberOfUse')}</label>
          <input
            type="number"
            className="form-control"
            name="numberOfUse"
            value={formData.numberOfUse}
            onChange={handleInputChange}
            placeholder={t('vouchers:placeholders.usageLimit')}
          />
        </div>

        {formData.voucherType === 0 && (
          <div className="mb-3 col-md-6">
            <label className="form-label">{t('vouchers:fields.maxAmount')}</label>
            <input
              type="number"
              className="form-control"
              name="maxAmount"
              value={formData.maxAmount}
              onChange={handleInputChange}
              placeholder={t('vouchers:placeholders.maxAmount')}
              step="0.01"
            />
            <small className="form-text text-muted">{t('vouchers:fields.maxAmountHint')}</small>
          </div>
        )}
        
        <div className="mb-3 col-md-3">
          <div className={`form-check mt-4 ${isRTL ? 'form-check-reverse' : ''}`}>
            <input
              type="checkbox"
              className="form-check-input"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              id="isActive"
            />
            <label className="form-check-label" htmlFor="isActive">
              {t('vouchers:fields.isActive')}
            </label>
          </div>
        </div>

        <div className="mb-3 col-md-3">
          <div className={`form-check mt-4 ${isRTL ? 'form-check-reverse' : ''}`}>
            <input
              type="checkbox"
              className="form-check-input"
              name="isPublic"
              checked={formData.isPublic}
              onChange={handleInputChange}
              id="isPublic"
            />
            <label className="form-check-label" htmlFor="isPublic">
              {t('vouchers:fields.isPublic')}
            </label>
          </div>
        </div>

        <div className="mb-3 col-md-3">
          <div className={`form-check mt-4 ${isRTL ? 'form-check-reverse' : ''}`}>
            <input
              type="checkbox"
              className="form-check-input"
              name="autoApply"
              checked={formData.autoApply}
              onChange={handleInputChange}
              id="autoApply"
            />
            <label className="form-check-label" htmlFor="autoApply">
              {t('vouchers:fields.autoApply')}
            </label>
          </div>
        </div>

        <div className="mb-3 col-md-12">
          <label className="form-label">{t('vouchers:table.customers')}</label>
          <Select
            isMulti
            value={selectedCustomers}
            onChange={handleCustomerChange}
            options={customerOptions}
            placeholder={t('common:select')}
            isSearchable
            isClearable
            isRtl={isRTL}
            closeMenuOnSelect={false}
            className="react-select-container"
            classNamePrefix="react-select"
            noOptionsMessage={() => t('common:noOptions', 'No options available')}
            loadingMessage={() => t('common:loading', 'Loading...')}
          />
        </div>
      </div>

      <div className="text-center">
        <button
          className="btn btn-primary"
          onClick={submitUpdate}
          disabled={loading}
        >
          {loading ? t('vouchers:buttons.updating') : t('vouchers:buttons.update')}
        </button>
      </div>
    </PageLayout>
  );
};
